import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  X,
  Home as HomeIcon,
  Check,
  BookOpen,
  Gift,
  Heart,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { getGlimpses, getGlimpseEntry, resolveMedia } from "@/api/client";
import { useLanguage } from "@/context/LanguageContext";

// Bilingual text picker: Hindi when available (with English fallback).
const tr = (obj, key, lang) => {
  if (!obj) return "";
  return (lang === "hi" ? obj[`${key}_hi`] || obj[key] : obj[key]) || "";
};

// Short category tags shown as a gold eyebrow pill above the headline. Kept
// concise (the card titles are already expressive hooks) to avoid duplication.
const EYEBROW = {
  STORY: "The Story",
  SURPRISE: "Did You Know",
  LENS: "The Bhāgavatam Lens",
  REFLECT: "Reflect",
  CURIOSITY: "There's More",
  "BHĀGAVATAM": "Only A Glimpse",
  BHAGAVATAM: "Only A Glimpse",
};

const CTA_ICONS = {
  book: BookOpen,
  home: HomeIcon,
  gift: Gift,
  heart: Heart,
  external: ExternalLink,
  sparkles: Sparkles,
  arrow: ArrowRight,
};

// ---- Entry card (rendered above the Vani videos on the Bhagavatam path) ----
// Navigates to the dedicated full-screen route instead of opening a modal.
export const GlimpseEntry = ({ slug }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { lang } = useLanguage();

  useEffect(() => {
    getGlimpseEntry()
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data || data.settings?.enabled === false || data.has_content === false)
    return null;
  const s = data.settings;
  const base = slug ? `/${slug}/bhagavatam/glimpse` : "/bhagavatam/glimpse";

  return (
    <div data-testid="glimpse-entry" className="mx-auto max-w-3xl px-5">
      <div className="overflow-hidden rounded-3xl border border-amber-300/25 bg-gradient-to-br from-[#221a3d] via-[#141a2e] to-[#0a0f24] p-8 text-center shadow-[0_0_60px_-20px_rgba(245,180,60,0.5)]">
        {s.entry_image ? (
          <div className="mx-auto mb-5 overflow-hidden rounded-2xl border border-amber-300/15">
            <img
              src={resolveMedia(s.entry_image)}
              alt={s.entry_image_alt || s.entry_title || ""}
              loading="eager"
              decoding="async"
              className="aspect-[16/9] w-full bg-[#141a2e] object-cover"
            />
          </div>
        ) : (
          <span className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-amber-400/15 text-amber-300">
            <Sparkles className="h-6 w-6" />
          </span>
        )}
        <h3 className="font-display text-3xl leading-tight text-amber-200 sm:text-4xl">
          {tr(s, "entry_title", lang)}
        </h3>
        <p className="mt-3 text-xs uppercase tracking-[0.25em] text-amber-100/45">
          {tr(s, "entry_meta", lang)}
        </p>
        <button
          data-testid="glimpse-explore-btn"
          onClick={() => navigate(base)}
          className="group mx-auto mt-6 inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-semibold text-[#0a0f24] transition-all hover:bg-amber-300"
        >
          {tr(s, "entry_cta", lang) || "Explore the Glimpse"}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// GlimpsePage — dedicated full-screen route.
// Routes: /bhagavatam/glimpse[/:feelingId]  and  /:slug/bhagavatam/glimpse[/:feelingId]
// Stages: feelings (grid) -> options (per feeling) -> cards (Primer swipe).
// Persistent navigation: ← back · progress · ⌂ home · ✕ close.
// ============================================================================
export const GlimpsePage = () => {
  const { slug, feelingId } = useParams();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();

  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [exp, setExp] = useState(null); // chosen experience (local state)
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [confirmExit, setConfirmExit] = useState(false);

  // Physical card-swipe motion: x follows the finger, rotate derives from x.
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-220, 0, 220], [-9, 0, 9]);
  const busy = useRef(false);

  const base = slug ? `/${slug}/bhagavatam/glimpse` : "/bhagavatam/glimpse";
  const micrositeHome = slug ? `/${slug}` : "/";

  useEffect(() => {
    getGlimpses()
      .then(setData)
      .catch(() => setError(true));
  }, []);

  // Reset per-feeling selection whenever the feeling in the URL changes.
  useEffect(() => {
    setExp(null);
    setIdx(0);
    setAnswer(null);
  }, [feelingId]);

  // Auto-enter the cards stage for feelings that have exactly one experience
  // (declarative — avoids the setTimeout/navigate race).
  useEffect(() => {
    if (!data) return;
    const f = feelingId
      ? (data.feelings || []).find((x) => x.feeling_id === feelingId)
      : null;
    if (f && !exp && (f.experiences || []).length === 1) {
      setExp(f.experiences[0]);
      setIdx(0);
      setAnswer(null);
    }
  }, [feelingId, exp, data]);

  if (error) {
    return (
      <FullScreen>
        <div className="grid flex-1 place-items-center px-6 text-center text-amber-100/70">
          <div>
            <p>We couldn&apos;t load this experience right now.</p>
            <button
              data-testid="glimpse-error-home"
              onClick={() => navigate(micrositeHome)}
              className="mt-5 rounded-full bg-amber-400 px-6 py-3 text-sm font-semibold text-[#0a0f24]"
            >
              Go back
            </button>
          </div>
        </div>
      </FullScreen>
    );
  }

  if (!data) {
    return (
      <FullScreen>
        <div className="grid flex-1 place-items-center text-amber-300">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
      </FullScreen>
    );
  }

  const settings = data.settings || {};
  const feelings = data.feelings || [];
  const feeling = feelingId
    ? feelings.find((f) => f.feeling_id === feelingId)
    : null;

  // If the feeling in the URL doesn't exist, fall back to feelings listing.
  if (feelingId && !feeling) {
    navigate(base, { replace: true });
    return null;
  }

  const stage = exp ? "cards" : feeling ? "options" : "feelings";
  const cards = exp?.cards || [];
  const card = cards[idx];
  const progress = cards.length ? ((idx + 1) / cards.length) * 100 : 0;
  const hasProgress = stage === "cards" && (idx > 0 || answer);

  // ---- navigation helpers ----
  const goFeeling = (f) => {
    // Navigate to the feeling route; the auto-enter effect handles the
    // single-experience case, otherwise the options stage is shown.
    navigate(`${base}/${f.feeling_id}`);
  };
  const startExp = (e) => {
    setExp(e);
    setIdx(0);
    setAnswer(null);
    x.set(0);
  };

  // Fly the active card off-screen, then swap to the neighbouring card.
  // sign < 0 => advance (card exits left); sign > 0 => go back (card exits right).
  const commit = (sign, { force } = {}) => {
    if (busy.current) return;
    if (sign < 0) {
      if (idx >= cards.length - 1) return springBack();
      if (!force && card?.type === "REFLECT" && !answer) return springBack();
    } else if (idx <= 0) {
      return springBack();
    }
    busy.current = true;
    const target = sign < 0 ? -window.innerWidth : window.innerWidth;
    animate(x, target, { duration: 0.24, ease: [0.4, 0, 1, 1] }).then(() => {
      setIdx((i) => (sign < 0 ? i + 1 : i - 1));
      x.set(0);
      busy.current = false;
    });
  };
  const springBack = () =>
    animate(x, 0, { type: "spring", stiffness: 420, damping: 34 });

  const onDragEnd = (e, info) => {
    const { offset, velocity } = info;
    const goNext =
      (offset.x < -90 || velocity.x < -650) &&
      idx < cards.length - 1 &&
      !(card?.type === "REFLECT" && !answer);
    const goPrev = (offset.x > 90 || velocity.x > 650) && idx > 0;
    if (goNext) commit(-1);
    else if (goPrev) commit(1);
    else springBack();
  };

  // Reflection: tap an answer -> show selected state -> auto-advance to feedback.
  const chooseAnswer = (key) => {
    if (answer) return;
    setAnswer(key);
    setTimeout(() => commit(-1, { force: true }), 620);
  };

  const back = () => {
    if (stage === "cards") {
      if (idx > 0) return commit(1);
      if ((feeling?.experiences || []).length > 1) return setExp(null);
      return navigate(base);
    }
    if (stage === "options") return navigate(base);
    // feelings root
    return navigate(micrositeHome);
  };

  const goHome = () => {
    const url =
      settings.home_destination === "custom" && settings.home_custom_url
        ? settings.home_custom_url
        : micrositeHome;
    if (/^https?:\/\//.test(url)) window.location.href = url;
    else navigate(url);
  };

  const doExit = () => {
    const dest = settings.exit_destination || "feelings";
    if (dest === "custom" && settings.exit_custom_url) {
      if (/^https?:\/\//.test(settings.exit_custom_url))
        window.location.href = settings.exit_custom_url;
      else navigate(settings.exit_custom_url);
    } else if (dest === "microsite_home") {
      navigate(micrositeHome);
    } else {
      // "feelings" default: back to the feelings listing (or microsite if already there)
      if (stage === "feelings") navigate(micrositeHome);
      else navigate(base);
    }
  };

  const close = () => {
    if (hasProgress) setConfirmExit(true);
    else doExit();
  };

  const runCTA = (cta) => {
    const url = (cta.url || "").trim();
    switch (cta.type) {
      case "internal":
      case "campaign":
        if (url.startsWith("/")) navigate(url);
        else if (url) window.open(url, "_blank", "noopener,noreferrer");
        break;
      case "microsite_home":
        navigate(url && url.startsWith("/") ? url : micrositeHome);
        break;
      case "whatsapp":
      case "external":
      default:
        if (url) window.open(url, cta.new_tab === false ? "_self" : "_blank", "noopener,noreferrer");
        else navigate(micrositeHome);
    }
  };

  return (
    <FullScreen>
      {/* ---- persistent header: ← ━━━ ⌂ ✕ ---- */}
      <div className="flex items-center gap-3 px-4 pt-4 sm:px-6 sm:pt-5">
        <button
          data-testid="glimpse-back"
          onClick={back}
          aria-label="Back"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/8 text-amber-100 transition-colors hover:bg-white/15"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
          <div
            data-testid="glimpse-progress"
            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-300"
            style={{ width: `${stage === "cards" ? progress : 0}%` }}
          />
        </div>

        <button
          data-testid="glimpse-home"
          onClick={goHome}
          aria-label="Home"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/8 text-amber-100 transition-colors hover:bg-white/15"
        >
          <HomeIcon className="h-4 w-4" />
        </button>
        <button
          data-testid="glimpse-lang-toggle"
          onClick={() => setLang(lang === "hi" ? "en" : "hi")}
          aria-label="Language"
          className="grid h-9 shrink-0 place-items-center rounded-full bg-white/8 px-3 text-xs font-semibold text-amber-100 transition-colors hover:bg-white/15"
        >
          {lang === "hi" ? "EN" : "हिं"}
        </button>
        <button
          data-testid="glimpse-close"
          onClick={close}
          aria-label="Close"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/8 text-amber-100 transition-colors hover:bg-white/15"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ---- body ---- */}
      <div className="flex flex-1 flex-col overflow-hidden px-4 pb-6 pt-5 sm:px-6">
        {stage === "feelings" && (
          <div className="mx-auto w-full max-w-3xl overflow-y-auto">
            <h2 className="text-center font-display text-3xl text-amber-200 sm:text-4xl">
              {lang === "hi" ? "आज आपके हृदय में क्या है?" : "What's in your heart today?"}
            </h2>
            <p className="mt-3 text-center text-sm text-amber-100/50">
              {tr(settings, "entry_meta", lang)}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {feelings.map((f) => (
                <button
                  key={f.feeling_id}
                  data-testid={`glimpse-feeling-${f.feeling_id}`}
                  onClick={() => goFeeling(f)}
                  className="flex flex-col items-center gap-2 rounded-2xl border border-amber-300/15 bg-white/[0.02] p-5 text-center transition-all hover:-translate-y-0.5 hover:border-amber-300/50 hover:bg-amber-400/10"
                >
                  <span className="text-3xl">{f.icon}</span>
                  <span className="text-sm text-amber-100/85">{tr(f, "label", lang)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {stage === "options" && feeling && (
          <div className="mx-auto w-full max-w-xl overflow-y-auto">
            <h2 className="text-center font-display text-3xl text-amber-200">
              <span className="mr-2">{feeling.icon}</span>
              {tr(feeling, "label", lang)}
            </h2>
            <div className="mt-8 space-y-3">
              {feeling.experiences.map((e) => (
                <button
                  key={e.experience_id}
                  data-testid={`glimpse-option-${e.experience_id}`}
                  onClick={() => startExp(e)}
                  className="w-full rounded-2xl border border-amber-300/20 bg-white/[0.02] p-5 text-left transition-all hover:border-amber-300/50 hover:bg-amber-400/10"
                >
                  <p className="font-display text-lg text-amber-200">
                    {tr(e, "option_title", lang)}
                  </p>
                  {tr(e, "option_hook", lang) && (
                    <p className="mt-2 text-sm text-amber-50/60">{tr(e, "option_hook", lang)}</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {stage === "cards" && card && (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* ---- card stack (one active card on top, peek cards behind) ---- */}
            <div className="flex flex-1 items-center justify-center overflow-hidden">
              <div className="relative h-full max-h-[600px] w-full max-w-md">
                {idx < cards.length - 1 && (
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-[28px] border border-white/[0.06] bg-gradient-to-b from-[#1a2140] to-[#0f1530]"
                    style={{ transform: "translateY(15px) scale(0.955)", opacity: 0.85 }}
                  />
                )}
                {idx < cards.length - 2 && (
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-[28px] border border-white/[0.05] bg-[#141a33]"
                    style={{ transform: "translateY(29px) scale(0.912)", opacity: 0.6 }}
                  />
                )}

                <motion.div
                  key={idx}
                  data-testid={`glimpse-card-${idx}`}
                  style={{
                    x,
                    rotate,
                    touchAction: "none",
                    "--g-title": `${settings.font_title || 24}px`,
                    "--g-body": `${settings.font_body || 16}px`,
                    "--g-eyebrow": `${settings.font_eyebrow || 11}px`,
                    "--g-cta": `${settings.font_cta || 14}px`,
                  }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.55}
                  onDragEnd={onDragEnd}
                  initial={{ opacity: 0, scale: 0.94, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 320, damping: 30 }}
                  className="absolute inset-0 z-10 flex cursor-grab flex-col overflow-hidden rounded-[28px] border border-amber-200/15 bg-gradient-to-b from-[#212a4d] via-[#161d3a] to-[#0e1430] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.85)] ring-1 ring-white/5 active:cursor-grabbing"
                >
                  {/* Edge-to-edge hero image (any card type) — flush to the top,
                      no padding, fills the rounded top corners; shrinks when the
                      text needs more room. */}
                  {card.show_image && card.image && (
                    <img
                      src={resolveMedia(card.image)}
                      alt={card.alt || ""}
                      draggable={false}
                      className="w-full shrink-0 select-none object-cover"
                      style={{ maxHeight: "40%" }}
                      data-testid={`glimpse-card-image-${idx}`}
                    />
                  )}
                  <div
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 py-6 sm:px-8"
                    style={{ touchAction: "pan-y" }}
                  >
                    <CardInner
                      card={card}
                      feeling={feeling}
                      answer={answer}
                      onChoose={chooseAnswer}
                      exp={exp}
                      runCTA={runCTA}
                      lang={lang}
                    />
                  </div>
                </motion.div>
              </div>
            </div>

            {/* ---- dot position indicator ---- */}
            <div className="mt-4 flex items-center justify-center gap-1.5">
              {cards.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? "w-5 bg-amber-300" : "w-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>

            {/* ---- persistent bottom navigation ---- */}
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                data-testid="glimpse-prev"
                onClick={() => commit(1)}
                disabled={idx === 0}
                className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 px-5 py-3 text-sm font-semibold text-amber-100 transition-all hover:bg-amber-400/10 disabled:pointer-events-none disabled:opacity-25"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {idx < cards.length - 1 ? (
                <button
                  data-testid="glimpse-next"
                  onClick={() => commit(-1)}
                  disabled={card.type === "REFLECT" && !answer}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3 text-sm font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-40"
                >
                  Next <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  data-testid="glimpse-done"
                  onClick={doExit}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3 text-sm font-semibold text-white transition-all hover:bg-emerald-400"
                >
                  Done <Check className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---- leave confirmation ---- */}
      <AnimatePresence>
        {confirmExit && (
          <motion.div
            data-testid="glimpse-confirm-exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10] grid place-items-center bg-black/60 px-6 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.94, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0 }}
              className="w-full max-w-xs rounded-3xl border border-amber-300/20 bg-[#141a2e] p-6 text-center"
            >
              <h4 className="font-display text-xl text-amber-200">
                Leave this experience?
              </h4>
              <p className="mt-2 text-sm text-amber-100/55">
                Your progress will not be saved.
              </p>
              <div className="mt-5 flex gap-3">
                <button
                  data-testid="glimpse-confirm-stay"
                  onClick={() => setConfirmExit(false)}
                  className="flex-1 rounded-full border border-amber-300/40 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/10"
                >
                  Continue
                </button>
                <button
                  data-testid="glimpse-confirm-leave"
                  onClick={() => {
                    setConfirmExit(false);
                    doExit();
                  }}
                  className="flex-1 rounded-full bg-amber-400 px-4 py-2.5 text-sm font-semibold text-[#0a0f24] hover:bg-amber-300"
                >
                  Exit
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </FullScreen>
  );
};

// Full-height wrapper using the stable --vph variable (never vh/h-screen) so the
// layout doesn't drift while in-app browsers animate their toolbars. A restrained
// radial glow + noise gives the dark stage atmospheric depth without clutter.
const FullScreen = ({ children }) => (
  <div
    data-testid="glimpse-page"
    className="fixed inset-0 z-[110] overflow-hidden bg-[#080c1a]"
    style={{ height: "var(--vph, 100vh)" }}
  >
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute left-1/2 top-[-12%] h-[55%] w-[130%] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(245,180,60,0.10),transparent_62%)] blur-2xl" />
      <div className="absolute bottom-[-18%] left-1/2 h-[55%] w-[130%] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(84,64,168,0.16),transparent_62%)] blur-2xl" />
    </div>
    <div className="relative z-10 flex h-full flex-col">{children}</div>
  </div>
);

// Gold eyebrow / category pill above the headline. Font size follows the
// Admin universal control via the --g-eyebrow CSS variable.
const Eyebrow = ({ children }) => (
  <span
    style={{ fontSize: "var(--g-eyebrow, 11px)" }}
    className="inline-flex w-fit items-center rounded-full border border-amber-300/25 bg-amber-400/[0.08] px-3 py-1 font-semibold uppercase tracking-[0.18em] text-amber-300"
  >
    {children}
  </span>
);

const Headline = ({ children, className = "" }) => (
  <h3
    style={{ fontSize: "var(--g-title, 24px)" }}
    className={`font-display font-semibold leading-[1.16] tracking-tight text-amber-100 ${className}`}
  >
    {children}
  </h3>
);

const Body = ({ children, className = "" }) => (
  <p
    style={{ fontSize: "var(--g-body, 16px)" }}
    className={`mt-4 whitespace-pre-line leading-relaxed text-amber-50/75 ${className}`}
  >
    {children}
  </p>
);

// ---------------------------------------------------------------------------
// CardInner — content-density adaptive renderer. Composition varies by card
// type; the shared design system (eyebrow · headline · body) stays consistent.
// ---------------------------------------------------------------------------
const CardInner = ({ card, feeling, answer, onChoose, exp, runCTA, lang }) => {
  const hasImage = card.show_image && !!card.image;
  const eyebrow = EYEBROW[card.type];
  const T = (obj, key) => tr(obj, key, lang);

  // FEEL — emotionally inviting, centred with the feeling icon.
  if (card.type === "FEEL") {
    return (
      <div className="flex min-h-full flex-col items-center justify-center text-center">
        {feeling?.icon && <span className="mb-5 text-5xl">{feeling.icon}</span>}
        {feeling?.label && <Eyebrow>{T(feeling, "label")}</Eyebrow>}
        <Headline className="mt-3">{T(card, "title")}</Headline>
        {T(card, "body") && <Body className="mx-auto max-w-[19rem]">{T(card, "body")}</Body>}
      </div>
    );
  }

  // REFLECT — question on top, options fill the central area; tap auto-advances.
  if (card.type === "REFLECT") {
    return (
      <div className="flex min-h-full flex-col">
        <Eyebrow>Reflect</Eyebrow>
        <Headline className="mt-3">{T(card, "title")}</Headline>
        <div className="mt-5 flex flex-1 flex-col justify-center gap-2.5 py-1">
          {(card.options || []).map((o) => {
            const selected = answer === o.key;
            return (
              <button
                key={o.key}
                data-testid={`glimpse-reflect-${o.key}`}
                disabled={!!answer}
                onClick={() => onChoose(o.key)}
                className={`flex w-full shrink-0 items-center gap-3 rounded-2xl border p-4 text-left transition-all ${
                  selected
                    ? "border-amber-300 bg-amber-400/15 shadow-[0_0_30px_-10px_rgba(245,180,60,0.6)]"
                    : "border-white/10 bg-white/[0.03] hover:border-amber-300/40 hover:bg-white/[0.06]"
                } ${answer && !selected ? "opacity-45" : ""}`}
              >
                <span
                  className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold transition-colors ${
                    selected ? "bg-amber-400 text-[#0a0f24]" : "bg-white/10 text-amber-200"
                  }`}
                >
                  {o.key}
                </span>
                <span className="text-sm leading-snug text-amber-50/85">{tr(o, "text", lang)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // FEEDBACK — rewarding moment of discovery with a soft glow + explanation.
  if (card.type === "FEEDBACK") {
    const correct = answer === card.correct_key;
    const explanation = (T(card, "body") || "")
      .replace(/^\s*correct answer:\s*[A-D][.)]?\s*/i, "")
      .trim();
    return (
      <div
        data-testid="glimpse-feedback-result"
        className="flex min-h-full flex-col items-center justify-center text-center"
      >
        <motion.span
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.05 }}
          className={`mb-5 grid h-16 w-16 place-items-center rounded-full ${
            correct
              ? "bg-emerald-400/15 text-emerald-300 shadow-[0_0_44px_-6px_rgba(52,211,153,0.6)]"
              : "bg-amber-400/15 text-amber-300 shadow-[0_0_44px_-6px_rgba(245,180,60,0.55)]"
          }`}
        >
          {correct ? <Check className="h-8 w-8" /> : <Sparkles className="h-7 w-7" />}
        </motion.span>
        <Headline>{correct ? T(card, "title") : lang === "hi" ? "एक कोमल संकेत" : "A GENTLE NUDGE"}</Headline>
        {explanation && <Body className="mx-auto max-w-[20rem]">{explanation}</Body>}
      </div>
    );
  }

  // BHĀGAVATAM — the conversion moment: prominent, centred, Admin-driven CTAs.
  if (card.type === "BHĀGAVATAM" || card.type === "BHAGAVATAM") {
    return (
      <div className="flex min-h-full flex-col items-center justify-center text-center">
        <Eyebrow>Only A Glimpse</Eyebrow>
        <Headline className="mt-3 text-amber-200">{T(card, "title")}</Headline>
        {T(card, "body") && <Body className="mx-auto max-w-[20rem]">{T(card, "body")}</Body>}
        <div className="mt-7 w-full max-w-xs space-y-3">
          {(exp.ctas || [])
            .filter((c) => c.enabled !== false)
            .map((cta, i) => {
              const Icon = CTA_ICONS[cta.icon] || ArrowRight;
              const primary = cta.primary || i === 0;
              return (
                <button
                  key={cta.id || i}
                  data-testid={`glimpse-cta-${cta.id || i}`}
                  onClick={() => runCTA(cta)}
                  style={{ fontSize: "var(--g-cta, 14px)" }}
                  className={
                    primary
                      ? "flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 font-semibold text-[#0a0f24] shadow-[0_10px_30px_-8px_rgba(245,180,60,0.7)] transition-all hover:bg-amber-300"
                      : "flex w-full items-center justify-center gap-2 rounded-full border border-amber-300/45 px-5 py-3 font-semibold text-amber-200 transition-all hover:bg-amber-400/10"
                  }
                >
                  <Icon className="h-4 w-4" /> {tr(cta, "label", lang)}
                </button>
              );
            })}
        </div>
      </div>
    );
  }

  // STORY / SURPRISE / LENS / CURIOSITY — narrative. The hero image (if any) is
  // rendered edge-to-edge at the top of the card by GlimpsePage; here we only lay
  // out the text — top-aligned when there's an image, else vertically centred.
  return (
    <div
      className={`flex min-h-full flex-col ${
        hasImage ? "justify-start pt-1 text-left" : "items-center justify-center text-center"
      }`}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Headline className="mt-3">{T(card, "title")}</Headline>
      {T(card, "body") && (
        <Body className={hasImage ? "" : "mx-auto max-w-[19rem]"}>{T(card, "body")}</Body>
      )}
    </div>
  );
};
